/* 🧪 VialRack — Nebula Vials inventory + active buffs panel (Mar 2026)
 * ---------------------------------------------------------------------
 * Shown in the owner's Profile and (compact mode) on the Battlegrounds
 * pre-match screen for quick drinks.
 *
 * Responsibilities:
 *   • Fetch /api/vials/state (inventory + active + totals)
 *   • Render an 8-color grid — one slot per color
 *   • Each slot shows highest-level owned + countdown of active buff
 *   • Click a level chip to drink that vial (POST /api/vials/consume)
 *   • Live countdown ticks every second
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ordering matches the spec: Primary → Secondary → Special utility
const COLOR_ORDER = ["red", "blue", "green", "gold", "purple", "orange", "silver", "emerald"];

// Local color metadata (icons/labels fall back to server payload, this is UI cosmetics)
const COLOR_META = {
  red:     { icon: "🔴", label: "Savage Might",     hex: "#DC2626", ring: "ring-red-500/50" },
  blue:    { icon: "🔵", label: "Supernova",        hex: "#3B82F6", ring: "ring-blue-500/50" },
  green:   { icon: "🟢", label: "Nirvana",          hex: "#10B981", ring: "ring-emerald-500/50" },
  gold:    { icon: "🟡", label: "Crit",             hex: "#EAB308", ring: "ring-yellow-500/50" },
  purple:  { icon: "🟣", label: "Block",            hex: "#A855F7", ring: "ring-purple-500/50" },
  orange:  { icon: "🟠", label: "Piercing",         hex: "#F59E0B", ring: "ring-amber-500/50" },
  silver:  { icon: "🩶", label: "Status Resist",    hex: "#94A3B8", ring: "ring-slate-400/50" },
  emerald: { icon: "💚", label: "Vampiric Block",   hex: "#059669", ring: "ring-emerald-600/60" },
};

const fmtSecs = (s) => {
  if (s == null || s < 0) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

export default function VialRack({ compact = false }) {
  const [state, setState]     = useState(null);
  const [busy, setBusy]       = useState(false);
  const [tick, setTick]       = useState(0);
  const [expanded, setExpanded] = useState(null);   // which color is "open" for level picking

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vials/state`);
      setState(r.data);
    } catch { /* silent — will retry on next interaction */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const drink = async (color, level) => {
    if (busy) return; setBusy(true);
    try {
      const r = await axios.post(`${API}/vials/consume`, { color, level });
      const b = r.data.bonuses || {};
      const parts = Object.entries(b).map(([k, v]) => `+${v} ${k}`).join(" · ");
      toast.success(`${COLOR_META[color]?.icon || "🧪"} ${color.toUpperCase()} Lv${level} — ${parts} · 60m`);
      await refresh();
      setExpanded(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't drink that vial.");
    } finally {
      setBusy(false);
    }
  };

  // Build a per-color view: { color, ownedByLevel:{1:n,...}, activeLevel, secsLeft, totalOwned }
  const rows = useMemo(() => {
    if (!state) return [];
    const activeByColor = {};
    for (const b of (state.active_buffs || [])) {
      activeByColor[b.color] = { level: b.level, seconds_left: b.seconds_left };
    }
    const invByColor = {};
    for (const item of (state.inventory?.items || [])) {
      invByColor[item.color] = invByColor[item.color] || {};
      invByColor[item.color][item.level] = (invByColor[item.color][item.level] || 0) + item.count;
    }
    return COLOR_ORDER.map(color => {
      const owned = invByColor[color] || {};
      const totalOwned = Object.values(owned).reduce((a, b) => a + b, 0);
      const highest = Math.max(0, ...Object.keys(owned).map(Number));
      const act = activeByColor[color];
      // The live countdown: tick the seconds_left down
      const secs = act ? Math.max(0, act.seconds_left - tick * 1) : 0;
      return {
        color,
        meta: COLOR_META[color],
        ownedByLevel: owned,
        totalOwned,
        highestOwned: highest,
        activeLevel:  act?.level || 0,
        secondsLeft:  act ? secs : 0,
      };
    });
  }, [state, tick]);

  if (!state) {
    return (
      <div className={compact ? "text-xs text-neutral-500 italic" : "text-sm text-neutral-500 italic p-6"}>
        Loading vial rack…
      </div>
    );
  }

  const nothing = rows.every(r => r.totalOwned === 0 && r.activeLevel === 0);

  return (
    <section
      data-testid="vial-rack"
      className={`bg-[#0F0F0F] border border-zinc-800 ${compact ? "p-3" : "p-6 mt-6"} rounded-lg`}
    >
      {!compact && (
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Consumables
            </div>
            <h2 className="font-display text-3xl text-white">🧪 Vial Rack</h2>
            <p className="text-xs text-neutral-400 mt-1 max-w-md">
              1-hour buffs. One active per color. Higher levels overwrite lower.
              Drop from Battleground chests + Guild Shop.
            </p>
          </div>
          <button
            onClick={refresh}
            className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-amber-300"
            data-testid="vial-rack-refresh"
          >
            ↻ Refresh
          </button>
        </div>
      )}

      {nothing ? (
        <div className="text-center py-6 text-sm text-neutral-500">
          Empty rack. Win Battleground chests or hit the Guild Shop to stock up.
        </div>
      ) : null}

      <div className={`grid ${compact ? "grid-cols-4 sm:grid-cols-8 gap-1.5" : "grid-cols-2 sm:grid-cols-4 gap-3"}`}>
        {rows.map(row => {
          const active = row.activeLevel > 0;
          const dim    = !active && row.totalOwned === 0;
          const open   = expanded === row.color;
          return (
            <div
              key={row.color}
              data-testid={`vial-slot-${row.color}`}
              className={`relative rounded-lg border-2 transition-all ${
                active
                  ? `border-amber-400/70 bg-gradient-to-br from-amber-950/40 to-black ring-2 ${row.meta.ring} shadow-[0_0_12px_rgba(245,158,11,0.35)]`
                  : dim
                    ? "border-zinc-800 bg-black/40 opacity-60"
                    : "border-zinc-700 bg-[#0A0A0A] hover:border-amber-700/60 cursor-pointer"
              } ${compact ? "p-2" : "p-3"}`}
              onClick={() => {
                if (row.totalOwned > 0) setExpanded(open ? null : row.color);
              }}
              title={`${row.meta.label} · ${active ? `Lv${row.activeLevel} active · ${fmtSecs(row.secondsLeft)}` : `${row.totalOwned} owned`}`}
            >
              <div className="flex items-center gap-2">
                <div className="text-2xl leading-none">{row.meta.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className={`${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-widest font-bold`}
                       style={{ color: row.meta.hex }}>
                    {row.color}
                  </div>
                  {!compact && (
                    <div className="text-[10px] text-neutral-400 truncate">{row.meta.label}</div>
                  )}
                </div>
              </div>
              {active ? (
                <div className={`mt-1 ${compact ? "text-[9px]" : "text-[10px]"} text-amber-300 font-bold`}>
                  Lv{row.activeLevel} · {fmtSecs(row.secondsLeft)}
                </div>
              ) : (
                <div className={`mt-1 ${compact ? "text-[9px]" : "text-[10px]"} text-neutral-500`}>
                  {row.totalOwned > 0
                    ? `${row.totalOwned} owned · max Lv${row.highestOwned}`
                    : "None"}
                </div>
              )}
              {/* Level picker popover */}
              {open && row.totalOwned > 0 && (
                <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-black border-2 border-amber-500/50 rounded-md p-2 shadow-xl">
                  <div className="text-[9px] uppercase tracking-widest text-amber-300 font-bold mb-1.5 text-center">
                    Drink which level?
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    {[1, 2, 3, 4, 5, 6].map(lv => {
                      const count = row.ownedByLevel[lv] || 0;
                      const disabled = count <= 0 || busy || (row.activeLevel > lv);
                      return (
                        <button
                          key={lv}
                          data-testid={`vial-drink-${row.color}-${lv}`}
                          disabled={disabled}
                          onClick={(e) => { e.stopPropagation(); drink(row.color, lv); }}
                          className={`text-[10px] py-1 rounded font-bold ${
                            disabled
                              ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
                              : "bg-amber-700/40 hover:bg-amber-600/60 text-amber-100"
                          }`}
                          title={row.activeLevel > lv
                                  ? `You have Lv${row.activeLevel} active — don't waste`
                                  : `Drink Lv${lv} (${count} owned)`}
                        >
                          Lv{lv} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Totals strip */}
      {!compact && state.totals && Object.keys(state.totals).length > 0 && (
        <div className="mt-4 pt-3 border-t border-zinc-800 flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-1">Active bonuses:</span>
          {Object.entries(state.totals).map(([stat, val]) => (
            <span key={stat}
                  className="text-[10px] font-bold text-amber-200 bg-amber-950/40 border border-amber-800/50 rounded-full px-2 py-0.5"
                  data-testid={`vial-total-${stat}`}>
              +{val} {stat}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
